import { purgeChunkCache } from './purgeChunkCache';

const STORAGE_KEY = 'chunk-reload-count';
const MAX_ATTEMPTS = 2;

/** @returns false when the budget is spent or sessionStorage is unreachable. */
function claimReloadAttempt(): boolean {
  try {
    const store = globalThis.sessionStorage;
    const attempts = Number(store.getItem(STORAGE_KEY) ?? 0);
    if (attempts >= MAX_ATTEMPTS) {
      return false;
    }
    store.setItem(STORAGE_KEY, String(attempts + 1));
    return true;
  } catch {
    return false;
  }
}

/**
 * Recovers a stale or corrupted client: drops the cached chunks, then hard-reloads.
 * Capped via sessionStorage (shared across recovery paths) so a broken build
 * can't reload-loop the user.
 * @returns true if recovery started, false if the cap was hit.
 */
export function reloadForChunkError(): boolean {
  if (!claimReloadAttempt()) {
    return false;
  }

  // Reloading alone re-reads the same cached bytes, so the purge has to land
  // first. A purge that fails still reloads: stale-skew clients recover without it.
  void purgeChunkCache()
    .catch(() => undefined)
    .then(() => globalThis.location.reload());

  return true;
}

/**
 * Clears the reload budget after a healthy mount, so the cap tracks
 * consecutive failures, not lifetime attempts for a tab left open for months.
 */
export function clearChunkErrorReloadBudget(): void {
  try {
    globalThis.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage inaccessible (privacy mode, disabled storage) — nothing to clear.
  }
}
